/**
 * Nextmine: "Share my collection".
 *
 * En samling er stykker en samler EJER og viser frem - ikke varer til salg.
 * Teknisk er hvert stykke en listing af typen `collection`, som kører på
 * inquiry-processen (samme motor som "In Search Of"). Det giver os gratis
 * billed-håndtering, en rigtig side pr. stykke og en indbakke-tråd, når en
 * besøgende spørger om stykket er til salg.
 *
 * Fordi de IKKE er varer, skal de holdes ude af alle salgsflader:
 * shop-søgningen (/s), forsidens "Recently listed" og profilens annonce-liste.
 * Brug helperne herunder i stedet for at gentage streng-sammenligninger.
 */

export const COLLECTION_LISTING_TYPE = 'collection';
export const ISO_LISTING_TYPE = 'in-search-of';

// Listing-typer der ikke er varer til salg, og derfor aldrig må dukke op i
// shop-søgningen eller blandt "Recently listed".
export const NON_SALE_LISTING_TYPES = [ISO_LISTING_TYPE, COLLECTION_LISTING_TYPE];

/** Listing-typen på en (evt. denormaliseret) listing. */
export const getListingType = listing => listing?.attributes?.publicData?.listingType;

export const isCollectionListing = listing => getListingType(listing) === COLLECTION_LISTING_TYPE;

export const isSaleListing = listing => !NON_SALE_LISTING_TYPES.includes(getListingType(listing));

/** Stykker en samler selv har fjernet, skjules (samme mønster som annoncer). */
export const isRemovedByOwner = listing => listing?.attributes?.publicData?.userDeleted === true;

/** Kun de stykker der reelt skal vises i en samling. */
export const visibleCollectionPieces = (listings = []) =>
  listings.filter(l => isCollectionListing(l) && !isRemovedByOwner(l) && !l?.attributes?.deleted);

/** Salgs-typernes id'er, som shop-søgningen må begrænses til. */
export const getSaleListingTypeIds = (listingTypes = []) =>
  listingTypes
    .filter(lt => !NON_SALE_LISTING_TYPES.includes(lt.listingType))
    .map(lt => lt.listingType);

/**
 * Typen en almindelig vare fødes med, når vi selv opretter en annonce (fx når
 * et samlingsstykke sættes til salg). Er der flere salgstyper, tager vi den
 * første - listing-wizarden lader hende alligevel skifte bagefter.
 */
export const getSaleListingTypeConfig = (listingTypes = []) =>
  listingTypes.find(lt => !NON_SALE_LISTING_TYPES.includes(lt.listingType)) || null;

/**
 * Konfigurationen for collection-typen, som den er sat op i Console.
 * Returnerer null hvis typen ikke findes endnu - så kan UI'et skjule funktionen
 * i stedet for at lave listings på en proces der ikke findes.
 */
export const getCollectionListingTypeConfig = (listingTypes = []) =>
  listingTypes.find(lt => lt.listingType === COLLECTION_LISTING_TYPE) || null;

export const isCollectionsEnabled = config =>
  !!getCollectionListingTypeConfig(config?.listing?.listingTypes);

/**
 * publicData som ethvert samlingsstykke skal fødes med. Processen/unitType
 * kommer fra Console-typen, præcis som listing-wizarden gør det for varer.
 */
export const collectionPublicData = (listingTypeConfig, extra = {}) => {
  const { transactionType = {}, listingType } = listingTypeConfig || {};
  const { alias, unitType } = transactionType;
  return {
    listingType: listingType || COLLECTION_LISTING_TYPE,
    ...(alias ? { transactionProcessAlias: alias } : {}),
    ...(unitType ? { unitType } : {}),
    ...extra,
  };
};
